import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Minus, Plus } from 'lucide-react';
import { currentOrdersScopeKey, useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { describeMarket } from '../../lib/kalshiTicker';
import {
  estimateAggregateFeeCents,
  takerRateForTicker,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { formatMoney } from '../../lib/infra/formatters';
import { classifyCloseResult } from '../../lib/trading/closeResidual';
import { selfTradePartialMessage } from '../../lib/trading/orderSubmissionOutcome';
import { workingCloses } from '../../lib/trading/workingCloses';
import { MarketTypeChip, restingCloseTitle } from '../../components/cards/cardKit';
import { isOscarBlockerOrder } from '../ValueDashboard/oscar';
import { isOrdersSyncFresh } from './ordersSyncStatus';
import { useCloseOrdersAuthority, useOrdersAreDeskWide, useOrdersForCloseCount } from '../../hooks/useOrdersAreDeskWide';
import { useSharedNow } from '../../lib/infra/sharedClock';
import { usePinnedKalshiOrderIds } from '../../hooks/usePinnedKalshiOrderIds';
import {
  kalshiFallbackTickCents,
  kalshiPriceBounds,
  stepKalshiPriceCents,
  validateKalshiPriceCentsInput,
} from '../../lib/pricing/subCent';

export interface EditPositionData {
  ticker: string;
  side: 'yes' | 'no';
  /** Full position contract count. Working closes (resting sells, and resting
   * opposite-side buys that Kalshi nets against this position) are read live
   * from the account store, not passed in. */
  position: number;
  /** Side-native average entry price in cents — shown for context only. */
  averagePrice: number;
  /** Side-native current mark in cents. Used as a fallback when the live
   * orderbook hasn't streamed yet so payout/fees populate immediately. */
  currentPriceCents?: number | null;
}

interface Props {
  data: EditPositionData;
  onClose: () => void;
  onPlaced?: () => void;
}

type OrderType = 'market' | 'limit';

function StepperInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  stepValue,
  suffix,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
  stepValue?: (value: number, direction: -1 | 1) => number | null;
  suffix?: string;
  autoFocus?: boolean;
}) {
  // Decimal-aware (sub-cent prices like "58.3" step to "59.3", not "59");
  // arithmetic in integer centi-cents so stepping never accrues float noise.
  const numVal = Number.parseFloat(value);
  const stepped = (n: number, d: number) => (Math.round(n * 100) + Math.round(d * 100)) / 100;
  const valueAfterStep = (direction: -1 | 1): number | null =>
    stepValue ? stepValue(numVal, direction) : (
      Number.isFinite(numVal) ? stepped(numVal, direction * step) : null
    );
  const decValue = valueAfterStep(-1);
  const incValue = valueAfterStep(1);
  const canDec =
    Number.isFinite(numVal) &&
    decValue !== null &&
    (min === undefined || decValue >= min);
  const canInc = incValue !== null && (max === undefined || incValue <= max);
  return (
    <div className="bg-muted border-border flex items-center rounded-lg border">
      <button
        type="button"
        onClick={() => canDec && onChange(String(decValue))}
        disabled={!canDec}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Minus size={14} />
      </button>
      <div className="relative flex-1">
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && canDec) {
              e.preventDefault();
              onChange(String(decValue));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (canInc) onChange(String(incValue));
              else if (value === '' || isNaN(numVal)) onChange(String(min ?? step));
            }
          }}
          autoFocus={autoFocus}
          className="text-foreground w-full bg-transparent py-2 text-center font-mono text-lg focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {suffix && value && (
          <span className="text-muted-foreground pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm">
            {suffix}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          if (value === '' || isNaN(numVal)) onChange(String(min ?? step));
          else if (canInc) onChange(String(incValue));
        }}
        disabled={!isNaN(numVal) && !canInc}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

type AccountSnapshot = ReturnType<typeof useAccountStore.getState>;

/** Contracts "close all" closes: the live held size when the positions list is
 *  synced with no failure since — over-asking is safe, the close is
 *  reduce-only — else the size the modal opened with. Gated on sync health,
 *  not age: positionsSync timestamps are receipt evidence, not a freshness
 *  deadline (see PositionsSyncState). A synced list without the row means the
 *  position is already flat. */
function closeAllQty(
  s: Pick<AccountSnapshot, 'positions' | 'positionsSync'>,
  ticker: string,
  side: 'yes' | 'no',
  openedWith: number,
): number {
  const live = s.positionsSync.synced && s.positionsSync.consecutiveFailures === 0;
  const held = live
    ? (s.positions.find((p) => p.ticker === ticker && p.side === side)?.position ?? 0)
    : openedWith;
  return Math.max(0, Math.floor(held));
}

export function EditPositionModal({ data, onClose, onPlaced }: Props) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);
  const sweepCloseLeg = useAccountStore((s) => s.sweepCloseLeg);
  const cancelOrders = useAccountStore((s) => s.cancelOrders);
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);
  const ordersLoaded = useAccountStore((s) => s.ordersSync.lastSuccessMs != null);
  const ordersSync = useAccountStore((s) => s.ordersSync);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const refreshPositions = useAccountStore((s) => s.refreshPositions);
  const positions = useAccountStore((s) => s.positions);
  const positionsSync = useAccountStore((s) => s.positionsSync);
  // A desk-wide list counts no resting closes (#961), so there is no count,
  // pre-fill offset, overshoot warning or close & cancel offer built from
  // orders that aren't the viewer's.
  const deskWideOrders = useOrdersAreDeskWide();
  const closeCountOrders = useOrdersForCloseCount();
  const closeOrdersAuthoritative = useCloseOrdersAuthority();
  const [openedScope] = useState(() => currentOrdersScopeKey());
  const accountUnchanged = openedScope !== null && openedScope === currentOrdersScopeKey();
  // Re-render on a clock so the offer disables when the orders list goes stale
  // with no store change to trigger it.
  const nowMs = useSharedNow(1_000);
  const getOrderbookRaw = useMarketStore((s) => s.getOrderbookRaw);
  const version = useMarketStore((s) => s.version);
  void version;

  const { ticker, side, position, averagePrice, currentPriceCents } = data;
  const restingCloses = useMemo(
    () => workingCloses({ ticker, side }, closeCountOrders, kalshiCancelStates),
    [ticker, side, closeCountOrders, kalshiCancelStates],
  );
  const restingCloseQty = restingCloses.qty;
  const OPP = side === 'yes' ? 'NO' : 'YES';
  // The cap is the whole position (#949). The close itself is reduce-only and
  // cannot pass flat; the only overshoot is resting orders filling AFTER it,
  // which the warning below names. Capping below the position would block the
  // legitimate "my limit close isn't filling — close it all now".
  const availableQty = Math.max(0, position);
  // #964: the badge carries an NFL ladder prop's player + rung on one line
  // (#603); its stat renders under it, so the badge never wraps its YES/NO.
  const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(ticker, side);
  const betTeam = badgeTeam ?? fullBetTeam;

  // Subscribe the position's ticker to the live orderbook stream while the
  // modal is open. PositionTracker only requests books for tickers with open
  // orders; without this, opening the modal on a held position with no
  // working orders would show a stale/null book.
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('edit-position-modal', [ticker]);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('edit-position-modal', []);
    };
  }, [ticker]);

  // Best bid on the side held — that's the price you'd hit by selling at
  // market. Same-side bids are sorted defensively since hydra's level order
  // isn't guaranteed.
  const bestBidCents = useMemo(() => {
    const book = getOrderbookRaw(ticker);
    if (!book) return null;
    const levels = side === 'yes' ? book.yes : book.no;
    if (!levels || levels.length === 0) return null;
    let max = -Infinity;
    for (const lvl of levels) if (lvl.price > max) max = lvl.price;
    return Number.isFinite(max) ? max : null;
    // Re-derive on book updates; getOrderbookRaw is a stable selector ref.
  }, [getOrderbookRaw, ticker, side, version]);
  const market = useMarketStore.getState().getMarket(ticker);
  const priceRanges = market?.priceRanges;
  // Unknown ranges ⇒ Kalshi's standard 1¢, or 0.1¢ when the live book already
  // rests a sub-cent level (Dash Bug 20096 — same rule as the order ticket).
  // Read per render: the store `version` subscription above re-renders on
  // every book update.
  const rawBook = getOrderbookRaw(ticker);
  const fallbackTickCents = kalshiFallbackTickCents(
    [...(rawBook?.yes ?? []), ...(rawBook?.no ?? [])].map((level) => level.price),
  );
  const priceBounds = kalshiPriceBounds(priceRanges, fallbackTickCents);

  const [orderType, setOrderType] = useState<OrderType>('market');
  // Pre-fill with what no resting order is already working to close, so the
  // default close can't overshoot flat. Seeded on open and, if the modal opened
  // before the first orders snapshot, re-seeded ONCE when it lands — unless the
  // trader has already edited qty. Also re-seeded, on the same condition, when
  // the desk-wide gate flips (#961 review): the count it was built from no
  // longer applies. Never tracks later order updates: a qty that moves under
  // the cursor could change what the next click closes.
  const prefillQty = String(Math.max(0, Math.floor(position - restingCloseQty)));
  const [qty, setQtyState] = useState(prefillQty);
  const qtyTouchedRef = useRef(false);
  const seededFromOrdersRef = useRef(ordersLoaded);
  const setQty = (v: string) => {
    qtyTouchedRef.current = true;
    setQtyState(v);
  };
  useEffect(() => {
    if (!ordersLoaded || seededFromOrdersRef.current) return;
    seededFromOrdersRef.current = true;
    if (!qtyTouchedRef.current) setQtyState(prefillQty);
  }, [ordersLoaded, prefillQty]);
  const countAvailable = !deskWideOrders && closeOrdersAuthoritative;
  const seededDeskWideRef = useRef(countAvailable);
  useEffect(() => {
    if (seededDeskWideRef.current === countAvailable) return;
    seededDeskWideRef.current = countAvailable;
    if (!qtyTouchedRef.current) setQtyState(prefillQty);
  }, [countAvailable, prefillQty]);
  const [confirmCancelResting, setConfirmCancelResting] = useState(false);
  const [price, setPrice] = useState(
    bestBidCents != null ? String(bestBidCents) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  // Synchronous re-entry guard for close & cancel: `submitting` only takes
  // effect on the next render.
  const closeAndCancelRunningRef = useRef(false);
  // Esc / backdrop / X are ignored while a close is running: dismissing mid-
  // flight would hide the outcome of real cancels and a real close.
  const requestClose = () => {
    if (!submitting) onClose();
  };
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Keep the limit price hint fresh until the user explicitly edits it.
  const [priceTouched, setPriceTouched] = useState(false);
  useEffect(() => {
    if (!priceTouched && bestBidCents != null && orderType === 'limit') {
      setPrice(String(bestBidCents));
    }
  }, [bestBidCents, priceTouched, orderType]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const qtyNum = parseInt(qty, 10);
  // Shared decimal parse + authoritative banded Kalshi grid. Missing metadata
  // uses the book-evidenced fallback tick above; typed prices are never
  // silently snapped.
  const priceCheck = validateKalshiPriceCentsInput(price, priceRanges, fallbackTickCents);
  const priceNum = priceCheck.ok ? priceCheck.priceCents : NaN;
  const canSubmit =
    accountUnchanged &&
    !submitting &&
    !isNaN(qtyNum) &&
    qtyNum >= 1 &&
    qtyNum <= availableQty &&
    (orderType === 'market' || priceCheck.ok);

  // Contracts past flat if this close fills AND every resting close fills too.
  // An upper bound: a resting reduce-only sell can't pass flat, but Kalshi reads
  // it back identically to an other-side buy, which can (see workingCloses).
  const overshootQty =
    !isNaN(qtyNum) && restingCloseQty > 0
      ? Number(Math.max(0, qtyNum + restingCloseQty - position).toFixed(2))
      : 0;

  const setQtyPercent = (pct: number) => {
    const q = Math.max(1, Math.round((availableQty * pct) / 100));
    setQty(String(q));
  };

  // Close & cancel always closes the WHOLE position (#949): it means "get out
  // now", and cancelling every resting close while closing only part would
  // leave the rest held with no working exit. Partial closes use plain Close.
  const fullQty = closeAllQty({ positions, positionsSync }, ticker, side, position);
  const blockerCount = restingCloses.orders.filter(isOscarBlockerOrder).length;
  // #943 — pins protect only from bulk sweeps. This cancels every resting close
  // (it can't skip one and still close safely), so it names the pinned ones.
  const pinnedIds = usePinnedKalshiOrderIds();
  const pinnedCount = restingCloses.orders.filter((o) => pinnedIds.has(o.orderId)).length;
  const cancelIncludes = [
    blockerCount > 0 ? `${blockerCount} Oscar blocker${blockerCount === 1 ? '' : 's'}` : null,
    pinnedCount > 0 ? `${pinnedCount} pinned` : null,
  ].filter(Boolean);
  // It acts on the order list, so the list must be current: openOrders keeps
  // the last good snapshot while sync fails.
  const ordersFresh = isOrdersSyncFresh(ordersSync, nowMs);
  // Recheck the captured account and snapshot authority at the click and
  // after asynchronous cancels. A render-time gate cannot authorize later work.
  const canCloseAndCancel =
    accountUnchanged &&
    closeOrdersAuthoritative &&
    !submitting &&
    !deskWideOrders &&
    ordersFresh &&
    restingCloses.orders.length > 0 &&
    fullQty >= 1 &&
    (orderType === 'market' || priceCheck.ok);
  const closeHow = orderType === 'limit' && priceCheck.ok
    ? `with a resting limit at ${priceNum}¢`
    : 'at market';

  /** Place the close itself and describe the outcome. */
  const placeClose = async (
    count: number,
  ): Promise<{ ok: boolean; msg: string; autoDismiss: boolean }> => {
    if (orderType === 'limit') {
      // Resting limit-to-close — single GTC order, no IOC residual semantics.
      // reduce_only so a stale count can't flip the position past flat.
      const placed = await placeOrder({
        ticker,
        side,
        action: 'sell',
        type: 'limit',
        count,
        price: priceNum,
        reduceOnly: true,
      });
      onPlaced?.();
      // #1229: a zero-fill self-trade cancel threw (runClose shows it). A
      // partial closed what it could and rests nothing: say so, keep the
      // modal up so the residual is acted on, never "Resting limit…".
      if (placed.selfTradeCancel) {
        return { ok: false, msg: selfTradePartialMessage(placed.selfTradeCancel), autoDismiss: false };
      }
      return { ok: true, msg: `Resting limit to close ${count} ${side.toUpperCase()}…`, autoDismiss: true };
    }
    // Market close: a single marketable IOC fills what the thin book holds
    // and CANCELS the rest, silently leaving residual exposure. sweepCloseLeg
    // verifies the fill and re-sweeps only what's left, then reports any
    // residual that survives all attempts so a panic-close can't quietly
    // leave the position open.
    const residual = await sweepCloseLeg(ticker, side, count);
    onPlaced?.();
    // Three distinct outcomes — null (UNVERIFIED) and 0 (confirmed flat) must
    // NOT be conflated: only a confirmed-flat close auto-dismisses; an
    // unverifiable or partial close keeps the modal up with a loud warning.
    return classifyCloseResult(residual, { ticker, side, quantity: count });
  };

  /** Shared submit shell: spinner, error surface, and the auto-dismiss when
   *  `step` reports a confirmed close. Callers gate on their own canSubmit. */
  const runClose = async (step: () => Promise<boolean>) => {
    setSubmitting(true);
    setResult(null);
    try {
      if (await step()) setTimeout(onClose, 900);
    } catch (err) {
      setResult({
        ok: false,
        msg: err instanceof Error ? err.message : 'Close failed',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (currentOrdersScopeKey() !== openedScope) {
      setResult({ ok: false, msg: 'The account changed — nothing was closed. Reopen this position to review it.' });
      return;
    }
    void runClose(async () => {
      const outcome = await placeClose(qtyNum);
      setResult({ ok: outcome.ok, msg: outcome.msg });
      return outcome.autoDismiss;
    });
  };

  /** #949 "close and cancel the resting other side". Cancel FIRST, and close
   *  only once every cancel is PROVEN: then no resting order can fill past flat
   *  after the reduce-only close, and one that fills mid-cancel only shrinks
   *  the position the close is capped by. Proven = our DELETE was accepted
   *  (`submitted`; the relay already maps "order already filled/cancelled" to
   *  success), or another surface's cancel for it was accepted
   *  (`already-pending` in awaiting-removal, or timed-out — reachable only if
   *  that cancel crosses the 5s sync window while our batch runs). Anything
   *  else — a failed DELETE, another cancel still in flight, or one whose
   *  state already cleared — aborts with nothing closed: the fresh-list
   *  fallback is unreliable (the org snapshot lags accepted cancels and can
   *  omit resting orders), and its failure mode is the overshoot itself.
   *  Every message after the cancels says how many exits are already gone. */
  const handleCloseAndCancel = () => {
    if (!canCloseAndCancel || closeAndCancelRunningRef.current) return;
    const actionScope = currentOrdersScopeKey();
    const sameAuthority = () => actionScope !== null && actionScope === openedScope
      && actionScope === currentOrdersScopeKey()
      && useAccountStore.getState().ordersScopeKey === actionScope;
    if (!sameAuthority()) {
      setConfirmCancelResting(false);
      setResult({ ok: false, msg: 'The account or order authority changed — nothing was cancelled. Reopen this position to review it.' });
      return;
    }
    if (!isOrdersSyncFresh(useAccountStore.getState().ordersSync, Date.now())) {
      setConfirmCancelResting(false);
      setResult({ ok: false, msg: 'The order list is not current — nothing was cancelled. Wait for it to sync and retry.' });
      return;
    }
    const targetIds = restingCloses.orders.map((o) => o.orderId);
    const total = targetIds.length;
    const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
    closeAndCancelRunningRef.current = true;
    void runClose(async () => {
      setConfirmCancelResting(false);
      // We refresh once ourselves below and read the list only after it lands.
      const results = await cancelOrders(targetIds, { refreshAfter: false });
      const cancelStates = useAccountStore.getState().kalshiCancelStates;
      const gone = (r: (typeof results)[number]) => {
        if (r.status === 'submitted') return true;
        const phase = cancelStates[r.orderId]?.phase;
        return r.status === 'already-pending' && (phase === 'awaiting-removal' || phase === 'timed-out');
      };
      const goneCount = results.filter(gone).length;
      const done = `Cancelled ${goneCount} of ${total} resting ${plural(total, 'close', 'closes')}`;
      // "Cancelled M of N resting closes; <what went wrong>" once any exit is
      // gone, so the trader knows those orders no longer protect the position.
      const after = (what: string) =>
        goneCount > 0 ? `${done}; ${what.charAt(0).toLowerCase()}${what.slice(1)}` : what;
      const abort = (what: string) => {
        setResult({ ok: false, msg: after(what) });
        return false;
      };

      const inFlight = results.filter(
        (r) => r.status === 'already-pending' && cancelStates[r.orderId]?.phase === 'requesting',
      );
      if (inFlight.length > 0) {
        return abort(
          `A cancel for ${inFlight.length} resting ${plural(inFlight.length, 'order', 'orders')} is still in progress — nothing was closed. Retry in a moment.`,
        );
      }
      const unproven = results.filter((r) => !gone(r));
      if (unproven.length > 0) {
        const reason = unproven.find((r) => r.status === 'failed' && r.error)?.error;
        return abort(
          `Could not cancel ${unproven.length} resting ${plural(unproven.length, 'order', 'orders')} — nothing was closed.${reason ? ` ${reason}` : ''}`,
        );
      }

      if (!sameAuthority()) return abort('The account or order authority changed — nothing was closed. Reopen this position to review it.');

      // A resting close placed after Confirm was never cancelled — don't close
      // past it blind. Read it from a list refreshed AFTER the cancels, and
      // only if that read succeeded. Best effort for orders placed on ANOTHER
      // device: on an org key the relay may serve a snapshot up to ~15 s old,
      // so an order placed elsewhere seconds earlier can be missing (orders
      // from this browser are in the list from placement). Positions refresh
      // alongside so the close is sized, or skipped, on the post-cancel holding.
      await Promise.all([refreshOrders(), refreshPositions().catch(() => false)]);
      const fresh = useAccountStore.getState();
      if (!sameAuthority()) return abort('The account or order authority changed — nothing was closed. Reopen this position to review it.');
      if (!isOrdersSyncFresh(fresh.ordersSync, Date.now())) {
        return abort('The order list could not be refreshed — nothing was closed. Retry once it syncs.');
      }
      const target = new Set(targetIds);
      const arrived = workingCloses({ ticker, side }, fresh.openOrders, fresh.kalshiCancelStates)
        .orders.filter((o) => !target.has(o.orderId));
      if (arrived.length > 0) {
        return abort(
          `${arrived.length} new resting ${plural(arrived.length, 'order that closes', 'orders that close')} this position appeared — nothing was closed. Review and retry.`,
        );
      }

      const count = closeAllQty(fresh, ticker, side, position);
      if (count < 1) {
        setResult({ ok: true, msg: `${done}; the position is already flat — nothing to close.` });
        return false;
      }
      let outcome: Awaited<ReturnType<typeof placeClose>>;
      try {
        outcome = await placeClose(count);
      } catch (err) {
        throw new Error(`${done}; the close failed: ${err instanceof Error ? err.message : 'unknown error'}`);
      }
      setResult({ ok: outcome.ok, msg: outcome.ok ? outcome.msg : `${done}; ${outcome.msg}` });
      return outcome.autoDismiss;
    }).finally(() => {
      closeAndCancelRunningRef.current = false;
    });
  };

  // Fill-price assumption for the breakdown:
  //   • LIMIT  → user-entered price, fee at maker rate (resting limit).
  //   • MARKET → live best bid, fee at taker rate (crosses the spread).
  //              Falls back to the side-native current mark when the book
  //              hasn't streamed yet so the user sees real numbers instead
  //              of dashes; banner still flags the book as not loaded.
  // If the limit happens to cross at fill time, taker would actually apply.
  // We don't know that ahead of time, so we keep the maker-rate estimate
  // here and accept the optimistic skew on crossing limits.
  const marketFillCents = bestBidCents ?? currentPriceCents ?? null;
  const fillPriceCents =
    orderType === 'limit' && !isNaN(priceNum) && priceNum > 0
      ? priceNum
      : orderType === 'market'
        ? marketFillCents
        : null;
  const safeQty = qtyNum > 0 ? qtyNum : 0;

  const closeRate =
    orderType === 'limit' ? makerRateForTicker(ticker) : takerRateForTicker(ticker);
  // Aggregate per-fill ceil — the figure Kalshi's own ticket quotes (fee
  // rounded up to the cent on the whole fill). No calibration coefficient:
  // the multi-fill premium it modeled does not exist.
  const feeCents =
    fillPriceCents != null && closeRate !== null && safeQty > 0
      ? estimateAggregateFeeCents({
          priceCents: fillPriceCents,
          count: safeQty,
          feeRate: closeRate,
        })
      : null;
  // MM Program rebate isn't folded into the close (sell) ticket yet — the
  // pre-trade buy surfaces carry it. See [[project_kalshi_mm_rebate_model]].
  const makerRebateCents = 0;

  // Sell-side analogues of the BUY widgets' four-field summary so the
  // shape matches GameDetailPanel + TradeModal: Price · Total Proceeds
  // (fee) · Max Payout · Max Profit.
  //   Total Proceeds = qty × price − close fee (what you net on the sale)
  //   Max Payout     = qty × $1     (the held-to-win alternative being
  //                                  given up by selling now)
  //   Max Profit     = Total Proceeds − cost basis at avg entry
  //                    (realized P&L on the sale; pre-loss avoidance
  //                    decision, not future-tense max upside)
  const grossProceedsCents =
    fillPriceCents != null ? fillPriceCents * safeQty : null;
  const totalProceedsCents =
    grossProceedsCents == null ? null : grossProceedsCents - (feeCents ?? 0) + makerRebateCents;
  const maxPayoutCents = safeQty > 0 ? safeQty * 100 : 0;
  const costBasisCents = averagePrice * safeQty;
  const maxProfitCents =
    totalProceedsCents == null ? null : totalProceedsCents - costBasisCents;
  // American odds for Max Payout reflect the all-in proceeds-per-contract
  // implied by selling now: $1 / (proceeds_per_contract). Mirrors the
  // BUY widgets' all-in-cost odds.
  const proceedsPerContractCents =
    totalProceedsCents == null || safeQty === 0
      ? null
      : totalProceedsCents / safeQty;
  const sellOddsLabel =
    proceedsPerContractCents == null ? '—' : americanOdds(proceedsPerContractCents);

  const fmt = (c: number | null) =>
    c == null ? '—' : formatMoney(c / 100);
  const fmtSigned = (c: number | null) =>
    c == null ? '—' : formatMoney(c / 100, { showSign: true });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={requestClose}
    >
      <div
        className="bg-card border-border max-h-[calc(100vh-2rem)] w-[420px] overflow-y-auto rounded-lg border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-foreground text-base font-bold tracking-wide">CLOSE POSITION</h2>
          <button
            onClick={requestClose}
            disabled={submitting}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {status !== 'connected' ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Connect your Kalshi account in Settings to trade
            </p>
          ) : (
            <>
              <div>
                <div className="flex items-center gap-2">
                  <span className="flex shrink-0 flex-col items-start gap-0.5">
                    <span
                      className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        side === 'yes'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {betTeam ? `(${betTeam}) ` : ''}{side.toUpperCase()}
                    </span>
                    <MarketTypeChip marketType={marketType} />
                  </span>
                  <span className="text-foreground text-sm">
                    Holding <span className="font-mono font-bold">{position}</span> @{' '}
                    <span className="font-mono">{averagePrice}¢</span>
                  </span>
                </div>
                <p className="text-muted-foreground mt-1 font-mono text-xs">{marketLabel}</p>
              </div>

              <div className="border-border rounded-lg border px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Order Type</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOrderType('market')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderType === 'market'
                          ? 'bg-blue-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      MARKET
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderType('limit')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderType === 'limit'
                          ? 'bg-blue-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      LIMIT
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-border rounded-lg border px-4 py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Qty</span>
                  <div className="flex items-center gap-1">
                    {[25, 50, 75, 100].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setQtyPercent(p)}
                        className="text-muted-foreground hover:text-foreground bg-muted rounded px-2 py-0.5 text-[10px] font-bold transition-colors"
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                </div>
                <StepperInput value={qty} onChange={setQty} min={1} max={availableQty} autoFocus />
                <p className="text-muted-foreground text-[11px]">
                  Max {availableQty} contracts
                  {restingCloseQty > 0 && (
                    <span className="text-amber-400 ml-1" title={restingCloseTitle(restingCloseQty)}>
                      ({Number(restingCloseQty.toFixed(2))} on resting orders that close this)
                    </span>
                  )}
                </p>
                {deskWideOrders && (
                  <p className="text-muted-foreground text-[11px]">
                    Resting closes aren’t counted and close &amp; cancel is off — the order
                    list covers the whole desk, including other traders’ and bot orders.
                  </p>
                )}
                {!deskWideOrders && !closeOrdersAuthoritative && (
                  <p className="text-muted-foreground text-[11px]">
                    Resting closes aren’t counted until orders are confirmed for this account.
                  </p>
                )}
                {!accountUnchanged && (
                  <p className="text-[11px] text-amber-400">
                    {openedScope === null ? 'Account not confirmed. Reopen this position after the account connects.' : 'Account changed. Reopen this position to review its current holding before closing.'}
                  </p>
                )}
                {overshootQty > 0 && (
                  <p role="alert" className="text-[11px] text-red-400">
                    ⚠ Closing {qtyNum} while {Number(restingCloseQty.toFixed(2))} rest to close
                    could take you {overshootQty} past flat — reversed into {OPP} — if they
                    all fill and any are buys of {OPP}.
                  </p>
                )}
              </div>

              {orderType === 'limit' && (
                <div className="border-border rounded-lg border px-4 py-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Price</span>
                    {bestBidCents != null && (
                      <span className="text-muted-foreground font-mono text-[11px]">
                        Best bid {bestBidCents}¢
                      </span>
                    )}
                  </div>
                  <StepperInput
                    value={price}
                    onChange={(v) => {
                      setPrice(v);
                      setPriceTouched(true);
                    }}
                    min={priceBounds.min}
                    max={priceBounds.max}
                    stepValue={(value, direction) =>
                      stepKalshiPriceCents(value, direction, priceRanges, fallbackTickCents)
                    }
                    suffix="¢"
                  />
                  {price !== '' && !priceCheck.ok && (
                    <p className="mt-1 text-[11px] text-red-400">{priceCheck.error}</p>
                  )}
                </div>
              )}

              <div className="border-border rounded-lg border px-4 py-3 space-y-1.5 text-sm">
                {(() => {
                  const isMarket = orderType === 'market';
                  const tip = isMarket
                    ? 'Market orders are not guaranteed at estimated price'
                    : undefined;
                  // Market orders prefix labels with "Est." since the
                  // fill price (best bid) can move before the order
                  // routes. Limit orders are deterministic on a fill.
                  const lbl = (s: string) => (isMarket ? `Est. ${s.toLowerCase()}` : s);
                  const feeLabel =
                    feeCents == null ? '—' : formatMoney(feeCents / 100);
                  return (
                    <>
                      {/* Price · Total Proceeds (fee) · Max Payout · Max
                          Profit — sell-semantic mirror of the BUY
                          widgets' four-field shape. Same field order
                          everywhere so the trader sees the same layout. */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground" title={tip}>
                          {lbl('Price')}
                        </span>
                        <span className="text-foreground font-mono inline-flex items-center gap-1.5">
                          <span>{fillPriceCents != null ? `${fillPriceCents}¢` : '—'}</span>
                          {fillPriceCents != null && (
                            <>
                              <span className="h-3 w-px self-center bg-current opacity-30" />
                              <span className="opacity-70">{americanOdds(fillPriceCents)}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground" title={tip}>
                          {lbl('Total Proceeds (fee)')}
                        </span>
                        <span className="text-foreground font-mono">
                          {fmt(totalProceedsCents)}{' '}
                          <span className="text-muted-foreground">({feeLabel})</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-muted-foreground"
                          title="Gross payout if held to win — $1 per contract. Selling now forfeits this."
                        >
                          Max Payout
                        </span>
                        <span className="text-positive font-mono inline-flex items-center gap-1.5">
                          <span>{fmt(maxPayoutCents)}</span>
                          {sellOddsLabel !== '—' && (
                            <>
                              <span className="h-3 w-px self-center bg-current opacity-30" />
                              <span className="opacity-70">{sellOddsLabel}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-muted-foreground"
                          title="Realized P&L on the sale = Total Proceeds − cost basis at avg entry"
                        >
                          {lbl('Max Profit')}
                        </span>
                        <span
                          className={`font-mono ${
                            maxProfitCents == null
                              ? 'text-muted-foreground'
                              : maxProfitCents >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {fmtSigned(maxProfitCents)}
                        </span>
                      </div>
                    </>
                  );
                })()}
                {orderType === 'market' && bestBidCents == null && (
                  <p className="text-amber-400 pt-1 text-[11px]">
                    Live book not loaded — market order will fill at whatever
                    bids are present when it routes.
                  </p>
                )}
              </div>

              {result && (
                <div
                  className={`rounded-md px-3 py-2 text-xs ${
                    result.ok
                      ? 'bg-green-500/10 text-green-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {result.msg}
                </div>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-red-600 hover:bg-red-700 disabled:bg-muted disabled:text-muted-foreground flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  `Close ${isNaN(qtyNum) ? '' : qtyNum} ${side.toUpperCase()}`
                )}
              </button>

              {restingCloses.orders.length > 0 &&
                (confirmCancelResting ? (
                  <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                    <p className="text-foreground">
                      Cancel {restingCloses.orders.length} resting order
                      {restingCloses.orders.length === 1 ? '' : 's'} (
                      {Number(restingCloseQty.toFixed(2))} contracts
                      {cancelIncludes.length > 0 ? `, incl. ${cancelIncludes.join(' and ')}` : ''}
                      ), then close all {fullQty} {side.toUpperCase()} {closeHow}?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCloseAndCancel}
                        disabled={!canCloseAndCancel}
                        className="flex-1 rounded bg-red-600 py-1.5 font-bold text-white hover:bg-red-700 disabled:bg-muted disabled:text-muted-foreground"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmCancelResting(false)}
                        className="bg-muted text-muted-foreground hover:text-foreground flex-1 rounded py-1.5 font-bold"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCancelResting(true)}
                    disabled={!canCloseAndCancel}
                    className="w-full rounded-lg border border-red-600/60 py-2 text-xs font-bold text-red-400 transition-colors hover:bg-red-600/10 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Close all {fullQty} &amp; cancel resting closes…
                  </button>
                ))}
              {restingCloses.orders.length > 0 && !ordersFresh && (
                <p className="text-muted-foreground text-[11px]">
                  Close &amp; cancel waits for a current order list — the last sync is stale or failing.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
